import { captureException } from '@sentry/react';
import { setTempLeadId } from 'domains/signupDirect/misc/leadStorage';
import {
  onClearSignupLocalStorage,
  onSaveSignupToLocalStorage,
  resumeSignupFromLocalStorage,
} from 'domains/signupDirect/misc/localStorage';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { extractFiltersFromURL } from 'domains/signupDirect/misc/URLparams';
import { getCommunicationConcernOptionsFromKeys } from 'domains/signupDirect/misc/communicationConcerns';
import { ResumedSignupData } from 'domains/signupDirect/ResumeLead/resumeLead';
import { EvaluationFormType, LoginContactInfoType } from 'domains/signupDirect/schemas/evaluationSchema';
import { DirectSignupFiltersFormType, directSignupFiltersSchema } from 'domains/signupDirect/schemas/filtersSchema';
import { findDefaultTimezoneOptionByState, getDefaultTimezoneOption } from 'domains/timezone';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { isObject, isUndefined } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DeepPartial } from 'react-hook-form';
import { Navigate, Outlet } from 'react-router-dom';
import { routes } from 'router/routes';
import { useTrack } from 'utils/hooks/useMixPanel';
import { useNavigation } from 'utils/hooks/useNavigation';
import { StateAbbrev } from 'utils/states';
import { EvaluationScheduleFormType } from './schemas/evaluationScheduleSchema';
import { filtersAllowSplitEvaluation } from './businessLogic/splitEvaluationCriteria';
import { useSplitEvaluationCtx } from './Evaluation/Schedule/Split/ByTherapist/SplitEvaluationsScheduleSection';
import { PrefilledSignupAppointment } from './ResumeLead/PrefilledAppointment/types';
import { useSaveUTMTrackers } from 'domains/tracking/UTMTracking';
import { useSplitEvaluationCtxV2 } from './Evaluation/Schedule/Split/BySlot/SplitEvaluationsScheduleSectionV2';
import { setupSignupSentryContext } from './sentry/signupSentry';
import { identifySignupLDUser } from './misc/launchDarklyTracking';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { SignupError } from 'utils/errors/debuggableError';
import { useCognitoUserContext } from 'domains/auth/AWSCognitoWrapper/context';
import { useContactById } from 'domains/contact/hooks';
import { Contact } from 'domains/contact/types';
import { JotformRedirectReason } from './misc/jotform/urls';
import { trackURLFilters } from './misc/urlFiltersTracking';

export type DirectSignupFilters = DirectSignupFiltersFormType;

export type ResumedLeadState = {
  shouldSkipEligibility: boolean;
  leadID: string;

  prefilledAppointment?: PrefilledSignupAppointment;
};

export type SignupDirectContext = {
  filters?: DirectSignupFilters;
  incompleteFiltersForm?: DeepPartial<DirectSignupFiltersFormType>;
  incompleteEvalForm?: DeepPartial<EvaluationFormType>;
  resumedLeadState?: ResumedLeadState;
  loginContactInfo?: LoginContactInfoType;

  evaluationSchedule?: EvaluationScheduleFormType;

  existingContactData:
    | {
        isLoading: true;
      }
    | {
        isLoading: false;
        contactData?: Contact;
      };

  onSubmitFilters: (newFilters: DirectSignupFilters) => void;
  onContinueToScheduleEval: () => void;
  onContinueToEvalForm: (timeSlot: EvaluationScheduleFormType) => void;
  /** @deprecated not in use at the moment, replaced with onContinueToJotformRegistrationForm */
  onContinueToConsultForm: (replace?: boolean) => void;
  onContinueToJotformRegistrationForm: (reason: JotformRedirectReason) => void;
  onSaveIncompleteEvalForm: (data?: DeepPartial<EvaluationFormType>) => void;

  onSuccessfulSignup: (loginContactInfo: LoginContactInfoType) => void;

  onResumeFromLeadData: (data: ResumedSignupData) => void;

  isResumedLead: boolean;

  onReturnToFilters: () => void;
  isLoadingSavedData: boolean;

  switchToPrivatePay: (
    filters: DirectSignupFilters | undefined,
    onSubmitFilters: (filters: DirectSignupFilters) => void,
    setHasSwitchedToPrivatePay: (value: boolean) => void,
  ) => void;
  hasSwitchedToPrivatePay: boolean;
  setHasSwitchedToPrivatePay: (hasSwitchedToPrivatePay: boolean) => void;

  jotformRedirectReason?: JotformRedirectReason;
};

export const SignupDirectContext = createContext<SignupDirectContext | null>(null);

export const useSignupDirectCtx = () => {
  const ctx = useContext(SignupDirectContext);

  if (!ctx) {
    throw new Error('Need to be used inside SignupDirectContext.Provider');
  }

  return ctx;
};

export const useSignupDirect = (): SignupDirectContext => {
  useSaveUTMTrackers();
  const { track } = useTrack();
  const { navigate } = useNavigation();

  const [filters, setFilters] = useState<DirectSignupFilters | undefined>();
  const [evalTimeSlot, setEvalTimeSlot] = useState<EvaluationScheduleFormType>();
  const [incompleteFiltersForm, setIncompleteFiltersForm] = useState<SignupDirectContext['incompleteFiltersForm']>();
  const [incompleteEvalForm, setIncompleteEvalForm] = useState<SignupDirectContext['incompleteEvalForm']>();
  const [isLoadingSavedData, setIsLoadingSavedData] = useState(true);
  const [resumedLeadState, setResumedLeadState] = useState<ResumedLeadState>();
  const [jotformRedirectReason, setJotformRedirectReason] = useState<JotformRedirectReason>();
  const [loginContactInfo, setLoginContactInfo] = useState<LoginContactInfoType>();

  const { setDisplayTimezone } = useDisplayTimezone();

  const cognito = useCognitoUserContext();
  const { data: contact, isLoading: isLoadingContact } = useContactById({
    contactId: cognito.isLoggedIn ? cognito.user.username : undefined,
  });

  const contactData = useMemo(() => {
    if (contact) {
      return {
        isLoading: false,
        contactData: contact,
      };
    }
    return {
      isLoading: isLoadingContact,
    };
  }, [contact, isLoadingContact]);

  const switchToPrivatePay = (
    filters: DirectSignupFilters | undefined,
    onSubmitFilters: (filters: DirectSignupFilters) => void,
    setHasSwitchedToPrivatePay: (value: boolean) => void,
  ) => {
    if (!filters) return;

    const normalizedFilters: DirectSignupFilters = {
      ...filters,
      paymentMethod: { type: 'privatePay' } as DirectSignupFilters['paymentMethod'],
    };

    setHasSwitchedToPrivatePay(true);

    onSubmitFilters(normalizedFilters);
  };

  const [hasSwitchedToPrivatePay, setHasSwitchedToPrivatePay] = useState<boolean>(false);

  const onSubmitFilters: SignupDirectContext['onSubmitFilters'] = useCallback(newFilters => {
    setFilters(newFilters);
    navigate(routes.signupDirect.landingPage);
    onUpdateDisplayTimezone(newFilters.stateCode);
  }, []);

  const onContinueToConsultForm: SignupDirectContext['onContinueToConsultForm'] = useCallback(replace => {
    navigate(routes.signupDirect.consultation.details, { replace });
  }, []);

  const onContinueToJotformRegistrationForm: SignupDirectContext['onContinueToJotformRegistrationForm'] = useCallback(
    reason => {
      if (!filters) throw new Error('Expected filters to be set');
      setJotformRedirectReason(reason);

      navigate(routes.signupDirect.jotformRedirect);
    },
    [filters],
  );

  const onContinueToScheduleEval: SignupDirectContext['onContinueToScheduleEval'] = useCallback(() => {
    if (!filters) throw new Error('Expected filters to be set');

    const filtersToCheck = filters;

    const clinicalConcerns = getCommunicationConcernOptionsFromKeys(filters.clinicalConcernKeys, filters.therapyTarget);

    if (filtersAllowSplitEvaluation({ paymentMethod: filtersToCheck.paymentMethod, clinicalConcerns }).allowed) {
      navigate(routes.signupDirect.evaluation.schedule.split.experimentFork);
    } else {
      navigate(routes.signupDirect.evaluation.schedule.single);
    }
  }, [filters]);

  const onContinueToEvalForm: SignupDirectContext['onContinueToEvalForm'] = useCallback(timeSlot => {
    setEvalTimeSlot(timeSlot);
    navigate(routes.signupDirect.evaluation.details);
  }, []);

  const onReturnToFilters: SignupDirectContext['onReturnToFilters'] = useCallback(() => {
    navigate(routes.signupDirect.filters);
  }, []);

  const onSaveIncompleteEvalForm: SignupDirectContext['onSaveIncompleteEvalForm'] = useCallback(data => {
    setIncompleteEvalForm(data);
  }, []);

  const onSuccessfulSignup: SignupDirectContext['onSuccessfulSignup'] = useCallback(loginContactInfo => {
    setLoginContactInfo(loginContactInfo);
    navigate(routes.signupDirect.completed);
  }, []);

  const onResumeFromLeadData: SignupDirectContext['onResumeFromLeadData'] = useCallback(data => {
    if (data.expired) {
      onClearSignupLocalStorage();
      navigate(routes.signupDirect.filters);
      return;
    }
    setTempLeadId(data.resumedLeadState.leadID);

    setResumedLeadState(data.resumedLeadState);

    if (data.resumedLeadState.prefilledAppointment) {
      if (isUndefined(data.filters.stateCode)) {
        captureException(
          new SignupError(
            `Missing state code for lead ${data.resumedLeadState.leadID} when using PREFILLED APPOINTMENT, ignoring appointment`,
            {
              debugData: {
                leadID: data.resumedLeadState.leadID,
                filters: data.filters,
              },
            },
          ),
        );
      } else {
        const appointment = data.resumedLeadState.prefilledAppointment;
        const defaultTz = findDefaultTimezoneOptionByState(data.filters.stateCode);
        setEvalTimeSlot({
          type: 'single',
          timeZone: defaultTz?.resolvedName,
          timeSlot: {
            time: appointment.appointmentTime,
            calendarID: appointment.calendarID,
            therapistName: `${appointment.therapistFirstName} ${appointment.therapistLastName}`,
            id: 'prefilled-appointment',
            therapistID: appointment.therapistID,
          },
        });
      }
    }

    if (data.incompleteEvalForm) {
      setIncompleteEvalForm(data.incompleteEvalForm);
    }
    if (data.filters) {
      const parsedFiltersForm = directSignupFiltersSchema.safeParse({
        ...data.filters,
      } as DirectSignupFiltersFormType);

      if (parsedFiltersForm.success) {
        // Filters are complete,
        setFilters(parsedFiltersForm.data as DirectSignupFilters);
        onUpdateDisplayTimezone(parsedFiltersForm.data.stateCode as StateAbbrev);

        navigate(routes.signupDirect.landingPage);
      } else {
        setIncompleteFiltersForm(data.filters);
        navigate(routes.signupDirect.filters);
      }
    }
    if (data.incompleteEvalForm) setIncompleteEvalForm(data.incompleteEvalForm);
  }, []);

  const onSaveToLocalStorage = () => {
    onSaveSignupToLocalStorage({
      filters,
      incompleteEvalForm,
      evalTimeSlot,
      resumedLeadState,
      loginContactInfo,
    });
  };

  useEffect(() => {
    if (!isLoadingSavedData) onSaveToLocalStorage();
  }, [filters, evalTimeSlot, incompleteEvalForm, isLoadingSavedData, evalTimeSlot, resumedLeadState]);

  const onUpdateDisplayTimezone = (stateCode: StateAbbrev) => {
    const deviceTzOption = getDefaultTimezoneOption();
    const stateTzOption = stateCode ? findDefaultTimezoneOptionByState(stateCode) : undefined;

    const selectedTimezone = deviceTzOption || stateTzOption;
    if (selectedTimezone) {
      setDisplayTimezone(selectedTimezone);
    }
  };

  const resumeFromLocalStorage = () => {
    const savedData = resumeSignupFromLocalStorage();

    if (savedData.filters) {
      onUpdateDisplayTimezone(savedData.filters.stateCode);
      setFilters(savedData.filters);
    }
    if (savedData.evalTimeSlot) setEvalTimeSlot(savedData.evalTimeSlot);
    if (savedData.incompleteEvalForm) setIncompleteEvalForm(savedData.incompleteEvalForm);
    if (savedData.resumedLeadState) setResumedLeadState(savedData.resumedLeadState);
  };

  const initialize = async () => {
    if (window.location.pathname === routes.signupDirect.resume) {
      setIsLoadingSavedData(false);
      return;
    }

    let filtersFromURL: DirectSignupFilters | undefined;
    try {
      filtersFromURL = await extractFiltersFromURL(window.location.search);
    } catch (e) {
      captureException(
        new SignupError('Failed to extract filters from URL', {
          debugData: {
            search: window.location.search,
            error: e,
          },
        }),
      );
    }

    if (filtersFromURL) {
      onUpdateDisplayTimezone(filtersFromURL.stateCode);
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.URL_FILTERS, { isResumedLead });
      trackURLFilters(track, filtersFromURL, isResumedLead);
      onSubmitFilters(filtersFromURL);
    } else {
      resumeFromLocalStorage();
    }
    setIsLoadingSavedData(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  const isResumedLead = isObject(resumedLeadState);

  const ctx = {
    filters,
    onSubmitFilters,
    onContinueToConsultForm,
    onContinueToScheduleEval,
    onContinueToEvalForm,
    onReturnToFilters,
    evaluationSchedule: evalTimeSlot,
    onSaveIncompleteEvalForm,

    incompleteEvalForm,
    incompleteFiltersForm,
    isLoadingSavedData,
    onResumeFromLeadData,
    isResumedLead,
    resumedLeadState,
    onContinueToJotformRegistrationForm,
    existingContactData: contactData,
    hasSwitchedToPrivatePay,
    switchToPrivatePay,
    setHasSwitchedToPrivatePay,
    jotformRedirectReason,
    onSuccessfulSignup,
    loginContactInfo,
  } satisfies SignupDirectContext;

  useEffect(() => {
    setupSignupSentryContext(ctx, resumedLeadState);
  }, [filters, evalTimeSlot, incompleteEvalForm, resumedLeadState]);

  const client = useLDClient();

  useEffect(() => {
    if (!client || !filters) {
      return;
    }
    identifySignupLDUser(client, filters, resumedLeadState);
  }, [filters, resumedLeadState, client]);

  return ctx;
};

/**
 * Short term solution for preventing opening the flow in a step its not supposed to start on
 */

export const FiltersGate = () => {
  return <Outlet />;
};

export const LandingPageGate = () => {
  const { filters } = useSignupDirectCtx();

  if (!filters) {
    return <Navigate to={routes.signupDirect.filters} />;
  }

  return <Outlet />;
};

export const EvalScheduleGate = () => {
  const { filters } = useSignupDirectCtx();

  if (!filters) {
    return <Navigate to={routes.signupDirect.filters} />;
  }

  return <Outlet />;
};

export const SecondSplitGate = () => {
  const { firstTimeSlot } = useSplitEvaluationCtx();

  if (!firstTimeSlot) {
    return <Navigate to={routes.signupDirect.evaluation.schedule.split.experimentFork} />;
  }

  return <Outlet />;
};

export const SecondSplitGateV2 = () => {
  const { firstTimeSlot } = useSplitEvaluationCtxV2();

  if (!firstTimeSlot) {
    return <Navigate to={routes.signupDirect.evaluation.schedule.split.experimentFork} />;
  }

  return <Outlet />;
};

export const PickTherapistGateV2 = () => {
  const { commonTherapists } = useSplitEvaluationCtxV2();

  if (!commonTherapists) {
    return <Navigate to={routes.signupDirect.evaluation.schedule.split.experimentFork} />;
  }

  return <Outlet />;
};

export const EvalFormGate = () => {
  const { evaluationSchedule, filters } = useSignupDirectCtx();

  if (!filters) {
    return <Navigate to={routes.signupDirect.filters} />;
  }

  if (!evaluationSchedule) {
    if (filtersAllowSplitEvaluation(filters).allowed) {
      return <Navigate to={routes.signupDirect.evaluation.schedule.split.experimentFork} />;
    } else {
      return <Navigate to={routes.signupDirect.evaluation.schedule.single} />;
    }
  }

  return <Outlet />;
};

export const ConsultFormGate = () => {
  const { filters } = useSignupDirectCtx();

  if (!filters) {
    return <Navigate to={routes.signupDirect.filters} />;
  }

  // Consultation scheduling has been disabled in favor of Jotform redirects.
  // If a user somehow reaches this route (via direct URL, bookmark, browser history, etc.),
  // redirect them to the landing page which will handle the proper routing:
  // - Eval-eligible users → appropriate schedule flow
  // - Non-eval-eligible users → Jotform redirect
  // This prevents users from accessing the deprecated consultation form.
  return <Navigate to={routes.signupDirect.landingPage} replace />;
};

export const SelfPayGate = () => {
  const { filters } = useSignupDirectCtx();

  if (!filters) {
    return <Navigate to={routes.signupDirect.filters} />;
  }

  return <Outlet />;
};
